import { orderBy } from 'lodash';
import uniqBy from 'lodash/uniqBy';

import { usersClient } from 'apis';
import {
  CarrierViewModel,
  DivisionViewModel,
  IEmployerViewModel,
  IUserDivisionRequest,
  PolicyDetailViewModel,
  PolicyStatus,
  PolicyViewModel,
  StatusType,
  TransactionType,
  UserViewModel,
} from 'apis/nswag';

import { cachedDivisions } from './division/cache';

export function addCommasToValue(value: number) {
  if (value === 0) {
    return '$0.00';
  }
  return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const SELECTED_FIRST_TYPE_CONTRIBUTION = 'Default';

export const insertSpaces = (string: string) => {
  string = string.replace(/([a-z])([A-Z])/g, '$1 $2');
  string = string.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
  return string;
};

export const ContributiontypesOptions = Object.keys(TransactionType)
  .filter((v) => isNaN(Number(v)) && v !== TransactionType.Default)
  .map((name) => {
    let label = insertSpaces(name);

    if (label.includes('Non Qual')) {
      label = label.replace('Non Qual', 'Non-Qual');
    }

    return {
      value: TransactionType[name as keyof typeof TransactionType],
      label: label,
    };
  });

export const SELECTED_FIRST_STATUS_TYPE = 'ActiveContributing';

export const StatusTypesOptions = Object.keys(StatusType)
  .filter((v) => isNaN(Number(v)) && v !== StatusType.Active && v !== StatusType.InActive)
  .map((name) => {
    let label = insertSpaces(name);

    if (label.includes('Active ')) {
      label = label.replace('Active ', 'Active - ');
    }

    if (label.includes('Terminated ')) {
      label = label.replace('Terminated ', 'Terminated - ');
    }

    if (label.includes('Retired ')) {
      label = label.replace('Retired ', 'Retired - ');
    }

    return {
      value: StatusType[name as keyof typeof StatusType],
      label: label,
    };
  });

export const sortOptions = (options: { value: string | number; label: string | undefined }[]) => {
  return options.sort((a, b) => a.label?.localeCompare(b.label || '') ?? 0);
};

export const getCarrierOptions = (carriers: CarrierViewModel[]) => {
  return sortOptions(
    uniqBy(carriers, 'id').map((carrier) => {
      return {
        label: carrier.name,
        value: carrier.id,
      };
    })
  );
};

export const getPolicyOptionsByCarrierId = (carrierId: number, policies: PolicyDetailViewModel[]) => {
  return sortOptions(
    uniqBy(policies, 'id')
      .filter((policy) => policy.carrierId === carrierId)
      .map((policy) => {
        return {
          label: policy.policyNumber,
          value: policy.id,
        };
      })
  );
};

export const getPolicyStatus = (policies: PolicyDetailViewModel[], policyId: number) => {
  const policy = policies.find((x) => x.id === policyId);
  if (policy && policy.status !== PolicyStatus.Active && policy.status !== PolicyStatus.Exchanged) {
    return ' (' + policy.status + ')';
  }
  return '';
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export const getComparator = (order: string, orderBy: string) => {
  return order === 'desc'
    ? (a: any, b: any) => descendingComparator(a, b, orderBy)
    : (a: any, b: any) => -descendingComparator(a, b, orderBy);
};

export function fillUpdatedPolicies(target: PolicyDetailViewModel[], source: PolicyViewModel[]) {
  if (!source) {
    return;
  }

  console.log('Filling updated policies', target, source);

  target.forEach((policy) => {
    const createdPolicy = source.find((p: any) => p.policyNumber === policy.policyNumber);
    if (createdPolicy) {
      policy.id = createdPolicy.id;
      policy.clientId = createdPolicy.clientId;
    }
    policy.allocations?.forEach((allocation) => {
      const createdAllocation = createdPolicy?.allocations?.find(
        (a) => a.investmentId === allocation.investmentId && a.percent === allocation.percent
      );
      if (createdAllocation) {
        allocation.id = createdAllocation.id;
      }
    });
    policy.beneficiaries?.forEach((beneficiary) => {
      const createdBeneficiary = createdPolicy?.beneficiaries?.find(
        (b) => b.name === beneficiary.name && b.type === beneficiary.type && b.percent === beneficiary.percent
      );
      if (createdBeneficiary) {
        beneficiary.id = createdBeneficiary.id;
      }
    });
  });
}

/**
 * Sorts the policies by their anniversary date and account label.
 * @param policies original
 * @param stringLabel indicate the list having "Account Label" of type number or string
 * @returns
 */
export function sortPolicies(policies: any[], stringLabel: boolean = false) {
  if (stringLabel) {
    // sort label by 'asc' because 'Benefit' > 'Recovery'
    return orderBy(policies, ['accountLabel', 'anniversaryDate'], ['asc', 'desc']);
  }
  return orderBy(policies, ['accountLabel', 'anniversaryDate'], ['desc', 'desc']);
}

export function sortPoliciesByAccountLabel<T extends { accountLabel: string | number }>(policies: T[]) {
  return orderBy(policies, ['accountLabel'], ['asc']);
}

export function isAsOfRequired(status: StatusType) {
  const validStatuses = [
    StatusType.TerminatedDeferredBenefit,
    StatusType.TerminatedReceivingBenefit,
    StatusType.TerminatedExhaustedBenefit,
    StatusType.RetiredDeferredBenefit,
    StatusType.RetiredReceivingBenefit,
    StatusType.RetiredExhaustedBenefit,
    StatusType.Deceased,
  ];
  return validStatuses.includes(status as StatusType);
}

export interface LocalOrg extends Pick<IEmployerViewModel, 'name'> {
  employerId: number;
  name: string;
  divisions: {
    divisionId: number;
    name: string;
  }[];
}
export function groupDivisionsByEmployer(divisions: DivisionViewModel[]): LocalOrg[] {
  const grouped: { [key: number]: LocalOrg } = {};

  divisions.forEach((division) => {
    if (!grouped[division.employerId]) {
      grouped[division.employerId] = {
        employerId: division.employerId,
        name: division.employer!.name!,
        divisions: [],
      };
    }
    grouped[division.employerId].divisions.push({
      divisionId: division.id,
      name: division.name!,
    });
  });

  return Object.values(grouped);
}

export function convertToDivisions(orgs: LocalOrg[]): IUserDivisionRequest[] {
  const ids = orgs.flatMap((o) => o.divisions.map((d) => d.divisionId));
  const divisions = ids.map((d) => cachedDivisions?.find((div) => div.id === d)).filter(Boolean);
  return divisions.map((div) => ({
    id: div!.id,
    name: div!.name,
    employerId: div!.employerId,
    employerName: div!.employer?.name || '',
  }));
}

const CACHE_TTL = 20000; // 20 seconds

type CacheEntry = {
  promise: Promise<UserViewModel>;
  timestamp: number;
};

const userCache: Record<string, CacheEntry> = {};

/**
 * Gets a user by ID from the cache or API. Only meant to be used in the UserDetailTab
 * since there is an unavoidable re-render and this API is expensive.
 * @param userId The ID of the user to fetch.
 * @returns A promise that resolves to the user data.
 */
export function getUserByIdCached(userId: string) {
  const now = Date.now();
  const entry = userCache[userId];

  if (entry && now - entry.timestamp < CACHE_TTL) {
    console.log(`Cache hit for user ${userId}`);
    return entry.promise;
  }

  console.log(`Cache miss for user ${userId}, fetching from API`);
  const promise = usersClient.getUserById(userId, true, false);
  userCache[userId] = { promise, timestamp: now };
  return promise;
}
